import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Id, OrderPODStatus } from '@hero/schema';
import { getObjectId } from '@hero/schema';
import {
  useCaptureLegsApi,
  useEligibleLegsApi,
  useInvoiceCarrierUploadInvoiceProofApi,
} from '@hero/api-hooks';
import { useEligibleLegsDrawerStore } from '@hero/hooks-shared';
import { fileToBase64 } from '@hero/web-utils';
import { toast } from 'sonner';
import { EditDrawer, type EditDrawerStep } from '../../../app/Drawer/EditDrawer';
import { DateField } from '../../../app/Date/DateField';
import { InvoiceNumberInput } from '../../../app/Input/InvoiceNumberInput';
import { SelectAuto, type SelectAutoOption } from '../../../app/Select/SelectAuto';
import { Label } from '../../../ui/Label';
import { VatFileField } from '../Shared/VatFileField';
import { EligibleOrderCard } from './EligibleOrderCard';

type StepId = 'select' | 'details';

/**
 * Subset of `OrderPODStatus` the picker exposes as user-facing
 * choices. The full 7-value enum still flows through the API,
 * but at create time the seller can really only declare these
 * three outcomes — everything else is a downstream transition.
 */
type OriginalCopyChoice = Extract<
  OrderPODStatus,
  'received' | 'not_received' | 'no_pod_required'
>;

const legKey = (orderId: Id, contractId: Id) =>
  `${String(orderId)}::${String(contractId)}`;

export interface EligibleLegsDrawerProps {
  /** Buyer-side filter for the picker — narrows eligible orders. */
  clientId?: Id;
  /**
   * Viewer's org id. Currently informational on the frontend — the
   * backend always pins the viewer's session as the seller — but we
   * keep it on the prop surface so consumers can pass it explicitly
   * from `useCurrentUser` and we never accidentally ask for orders the
   * viewer doesn't own.
   */
  viewerOrgId?: Id;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Called after a successful capture with the resulting batch
   * summary. `created` is 0 or 1 (single invoice for the batch);
   * `skipped` is the per-leg skip count.
   */
  onCreated?: (info: { created: number; skipped: number }) => void;
}

/**
 * Seller-only picker that turns delivered orders into a SINGLE
 * invoice.
 *
 * Two-step flow:
 *  1. **Select** — multi-select the delivered orders that should
 *     all be bundled into one invoice. Each row is an
 *     {@link EligibleOrderCard} with route / pickup date / price
 *     (with optional "+ fees" tick), a POD status chip showing
 *     the paper-trail state of the order, and an eye button that
 *     opens the underlying `OrderDrawer` for inline price edits.
 *     Price changes there propagate back into this picker via
 *     the shared `eligibleLegs` cache invalidation.
 *  2. **Details** — pick the required expected pay date, an
 *     optional invoice number (renders with a `#` glyph), and
 *     the original-copy lifecycle status for the batch
 *     (defaulted from whether any selected order requires a
 *     paper-original POD). These apply to the SINGLE resulting
 *     invoice — multi-select means "bundle these into one
 *     invoice", not "create N invoices".
 *
 * The drawer reads {@link useEligibleLegsApi} (server pins viewer =
 * seller, filters out already-captured orders) and submits via
 * {@link useCaptureLegsApi}. See `EditDrawer` for the underlying
 * step machinery / chrome.
 */
export const EligibleLegsDrawer: React.FC<EligibleLegsDrawerProps> = ({
  clientId,
  open,
  onOpenChange,
  onCreated,
}) => {
  const { t } = useTranslation(['m-invoice', 'c-main']);

  const {
    data: eligible,
    isLoading,
    refetch,
  } = useEligibleLegsApi(
    { clientId: clientId ? getObjectId(clientId) : undefined },
    { enabled: open },
  );

  const legs = eligible?.legs ?? [];

  const [step, setStep] = useState<StepId>('select');
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  // Optional — when left blank, the backend falls back to the
  // matching Agreement / payment-terms schedule. No prefill so the
  // seller doesn't silently accept "today + 30".
  const [expectedDueDate, setExpectedDueDate] = useState<Date | undefined>(
    undefined,
  );
  const [invoiceNumber, setInvoiceNumber] = useState<string>('');
  // `undefined` until the user opens step 2 so we can auto-derive
  // the default from the selected orders' `requiresOriginalCopy`
  // flag without fighting a user override.
  const [originalCopyStatus, setOriginalCopyStatus] = useState<
    OriginalCopyChoice | undefined
  >(undefined);
  // Optional VAT PDF/image. If present and the capture succeeded,
  // we chain a carrier upload-proof mutation on the new invoice
  // so the user can finish "create invoice + attach VAT" in one
  // pass (the standalone UploadInvoiceProofDrawer still exists
  // for later uploads / re-uploads).
  const [vatFiles, setVatFiles] = useState<File[]>([]);

  // Reset local state when the drawer closes so reopening on a
  // different client starts fresh.
  useEffect(() => {
    if (!open) {
      setStep('select');
      setSelected({});
      setInvoiceNumber('');
      setExpectedDueDate(undefined);
      setOriginalCopyStatus(undefined);
      setVatFiles([]);
    }
  }, [open]);

  // Compute the default original-copy status from the selected
  // legs. If ANY selected leg requires a paper-original return,
  // the new invoice defaults to `'not_received'`; otherwise
  // `'no_pod_required'`. Re-evaluates as selection changes, but
  // a user override (state set non-undefined) wins.
  const selectedLegs = useMemo(
    () => legs.filter(leg => selected[legKey(leg.orderId, leg.contractId)]),
    [legs, selected],
  );
  const defaultedOriginalCopyStatus: OriginalCopyChoice = useMemo(() => {
    const anyRequiresCopy = selectedLegs.some(l => l.requiresOriginalCopy);
    return anyRequiresCopy ? 'not_received' : 'no_pod_required';
  }, [selectedLegs]);
  const effectiveOriginalCopyStatus: OriginalCopyChoice =
    originalCopyStatus ?? defaultedOriginalCopyStatus;

  const uploadProof = useInvoiceCarrierUploadInvoiceProofApi({
    // Errors here are non-fatal — the invoice was already
    // created. Surface the failure so the user can retry from
    // the dedicated upload drawer on the invoice, but keep the
    // drawer flow positive.
    onError: (err: Error) => {
      toast.error(
        t('m-invoice:create_invoice.vat_upload_failed', {
          message: err.message,
          defaultValue: `Invoice created but VAT upload failed: ${err.message}`,
        }),
      );
    },
  });

  const captureLegs = useCaptureLegsApi({
    onSuccess: async data => {
      const createdInvoice = data?.created;
      const createdCount = createdInvoice ? 1 : 0;
      const skipped = data?.skipped?.length ?? 0;

      // Chain the optional VAT upload — only when capture
      // returned an invoice AND the user actually picked a PDF.
      // Non-PDFs are blocked at submit time by `detailsValid`,
      // but the guard here is belt-and-braces in case someone
      // calls the mutation through an alternate path. Anything
      // missing just means "they can upload it later from the
      // invoice drawer".
      if (
        createdInvoice &&
        vatFiles[0] &&
        vatFiles[0].type === 'application/pdf'
      ) {
        try {
          const base64 = await fileToBase64(vatFiles[0]);
          // Fall back to the invoice's own ref when the user
          // didn't type an external invoice number — the
          // upload-proof API requires a `refNumber`, and using
          // the platform ref is a safer placeholder than
          // refusing the upload.
          const refForUpload =
            invoiceNumber.trim() ||
            createdInvoice.ref?.main ||
            createdInvoice.ref?.short ||
            '';
          if (refForUpload) {
            await uploadProof.mutateAsync({
              invoiceId: getObjectId(createdInvoice._id),
              image: { upload: base64 },
              refNumber: refForUpload,
            });
          }
        } catch {
          // already surfaced by the mutation's onError; swallow
          // the rejection here so the success toast still fires.
        }
      }

      toast.success(
        t('m-invoice:create_invoice.success', {
          skipped,
          defaultValue:
            createdCount === 1
              ? `Invoice created.${skipped > 0 ? ` ${skipped} order(s) skipped.` : ''}`
              : 'No invoice was created.',
        }),
      );
      void refetch();
      onCreated?.({ created: createdCount, skipped });
      if (createdCount > 0) onOpenChange(false);
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const selectedCount = useMemo(
    () => Object.values(selected).filter(Boolean).length,
    [selected],
  );

  // ── Conditional "you already have this invoice" detection ──
  // Any one of these signals means the seller is declaring the
  // invoice exists in their accounting system right now, which
  // promotes the full triplet (PDF + ref + expected pay date)
  // from "optional convenience" to "required for consistency":
  //
  //   - Typed an invoice number
  //   - Picked a VAT file
  //   - Marked the original copy as already "received by buyer"
  //     (the buyer can't have received a paper original if the
  //     invoice itself wasn't issued).
  //
  // When this is off, all three fields stay optional — the user
  // is just queuing a Hero-issued invoice and can finish the VAT
  // paperwork later from the Invoice drawer.
  const trimmedInvoiceNumber = invoiceNumber.trim();
  const hasInvoiceNumber = trimmedInvoiceNumber.length > 0;
  const hasVatFile = vatFiles.length > 0;
  const declaredReceived = originalCopyStatus === 'received';
  const declaredInvoiceExists =
    hasInvoiceNumber || hasVatFile || declaredReceived;

  // PDF check is also a hard gate: the FileUploader's `accept`
  // restriction is best-effort (drag-and-drop bypasses MIME), so
  // we re-validate in JS and surface an inline error.
  const vatFileIsPdf =
    !hasVatFile || vatFiles[0]?.type === 'application/pdf';

  const detailsValid =
    !declaredInvoiceExists ||
    (hasInvoiceNumber && hasVatFile && vatFileIsPdf && !!expectedDueDate);

  const toggle = (orderId: Id, contractId: Id) => {
    const key = legKey(orderId, contractId);
    setSelected(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = async () => {
    if (selectedLegs.length === 0 || !detailsValid) return;
    const items = selectedLegs.map(leg => ({
      orderId: getObjectId(leg.orderId),
      contractId: getObjectId(leg.contractId),
    }));
    await captureLegs.mutateAsync({
      legs: items,
      // Optional — when omitted, the backend keeps the
      // agreement-driven schedule the DTO computed at create time.
      expectedDueDate,
      invoiceNumber: trimmedInvoiceNumber || undefined,
      originalCopyStatus: effectiveOriginalCopyStatus,
    });
  };

  const selectStep: EditDrawerStep = {
    id: 'select',
    title: t('m-invoice:create_invoice.drawer_title', {
      defaultValue: 'Create invoice from delivered orders',
    }),
    description: t('m-invoice:create_invoice.drawer_description', {
      defaultValue:
        'Select one or more delivered orders to bundle into a single invoice.',
    }),
    primaryLabel: t('m-invoice:create_invoice.continue', {
      count: selectedCount,
      defaultValue:
        selectedCount > 0
          ? `Continue (${selectedCount} order${selectedCount === 1 ? '' : 's'})`
          : 'Continue',
    }),
    primaryDisabled: selectedCount === 0 || isLoading,
    content: (
      <div className="flex flex-col gap-3">
        {isLoading && (
          <div className="rounded-md border border-gray-200 p-4 text-sm text-gray-500">
            {t('c-main:async_operations.loading', { defaultValue: 'Loading...' })}
          </div>
        )}

        {!isLoading && legs.length === 0 && (
          <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-600">
            {t('m-invoice:create_invoice.empty_eligible', {
              defaultValue:
                'No delivered orders are waiting to be invoiced for this client.',
            })}
          </div>
        )}

        {!isLoading && legs.length > 0 && (
          <div className="flex flex-col gap-2">
            {legs.map(leg => {
              const key = legKey(leg.orderId, leg.contractId);
              return (
                <EligibleOrderCard
                  key={key}
                  leg={leg}
                  selected={!!selected[key]}
                  onToggleSelected={() => toggle(leg.orderId, leg.contractId)}
                />
              );
            })}
          </div>
        )}
      </div>
    ),
  };

  // Per-option descriptions are surfaced in the dropdown so the
  // seller understands "Original copy" as the whole-invoice
  // promise to ship paper PODs back with the invoice — all of
  // the selected orders inherit this status.
  const copyOptions: SelectAutoOption[] = [
    {
      value: 'received',
      label: t('m-invoice:original_copy.received', {
        defaultValue: 'Received by buyer',
      }),
      description: t('m-invoice:original_copy.received_description', {
        defaultValue:
          'Paper PODs for every order on this invoice are already with the buyer.',
      }),
    },
    {
      value: 'not_received',
      label: t('m-invoice:original_copy.not_received_yet', {
        defaultValue: 'Not received yet',
      }),
      description: t('m-invoice:original_copy.not_received_yet_description', {
        defaultValue:
          "You'll return original PODs for every order on this invoice. They haven't reached the buyer yet.",
      }),
    },
    {
      value: 'no_pod_required',
      label: t('m-invoice:original_copy.not_required', {
        defaultValue: 'Not required',
      }),
      description: t('m-invoice:original_copy.not_required_description', {
        defaultValue:
          'No paper PODs need to be returned with this invoice.',
      }),
    },
  ];

  const detailsStep: EditDrawerStep = {
    id: 'details',
    title: t('m-invoice:create_invoice.details_title', {
      defaultValue: 'Invoice details',
    }),
    description: t('m-invoice:create_invoice.details_description', {
      count: selectedCount,
      defaultValue:
        selectedCount === 1
          ? 'One invoice will be created from this order.'
          : `One invoice will be created from ${selectedCount} orders.`,
    }),
    primaryLabel: t('m-invoice:create_invoice.submit', {
      defaultValue: 'Create invoice',
    }),
    primaryDisabled: selectedCount === 0 || !detailsValid,
    onPrimary: handleSubmit,
    content: (
      <div className="flex flex-col gap-4">
        {declaredInvoiceExists && (
          <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-xs text-fg">
            {t('m-invoice:create_invoice.declared_existing_hint', {
              defaultValue:
                "Looks like you've already issued this invoice in your accounting system. Please supply the PDF, invoice number, and expected pay date so the records match.",
            })}
          </div>
        )}

        <div className="flex flex-col gap-1">
          <DateField
            label={t('m-invoice:create_invoice.expected_due_date', {
              defaultValue: 'Expected pay date',
            })}
            required={declaredInvoiceExists}
            value={expectedDueDate ?? null}
            onChange={setExpectedDueDate}
            placeholder={t('m-invoice:create_invoice.expected_due_date_placeholder', {
              defaultValue: 'Optional — leave blank to use the agreement schedule',
            })}
          />
          <p className="text-xs text-muted-foreground">
            {declaredInvoiceExists
              ? t('m-invoice:create_invoice.expected_due_date_required_hint', {
                  defaultValue:
                    "Required because you've supplied invoice details. This pay date matches the invoice you already issued.",
                })
              : t('m-invoice:create_invoice.expected_due_date_hint', {
                  defaultValue:
                    "Optional. When left blank we use the matching agreement's payment terms.",
                })}
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="eligible-invoice-number">
            {t('m-invoice:create_invoice.invoice_number_label', {
              defaultValue: 'Invoice number',
            })}
            {declaredInvoiceExists && (
              <span className="text-destructive ms-1">*</span>
            )}
          </Label>
          <InvoiceNumberInput
            id="eligible-invoice-number"
            value={invoiceNumber}
            onChange={setInvoiceNumber}
            placeholder={
              declaredInvoiceExists
                ? t('m-invoice:create_invoice.invoice_number_required_placeholder', {
                    defaultValue: 'Required — your accounting reference',
                  })
                : t('m-invoice:create_invoice.invoice_number_placeholder', {
                    defaultValue: 'Optional — your accounting reference',
                  })
            }
          />
          <p className="text-xs text-muted-foreground">
            {declaredInvoiceExists
              ? t('m-invoice:create_invoice.invoice_number_required_hint', {
                  defaultValue:
                    "Required because you've supplied invoice details. Use the same reference as your accounting system.",
                })
              : t('m-invoice:create_invoice.invoice_number_hint', {
                  defaultValue:
                    'Optional. Add later from the VAT upload step on the invoice.',
                })}
          </p>
        </div>

        <SelectAuto
          label={t('m-invoice:original_copy.label_status', {
            defaultValue: 'Original copy',
          })}
          options={copyOptions}
          value={effectiveOriginalCopyStatus}
          onChange={next =>
            setOriginalCopyStatus(next as OriginalCopyChoice | undefined)
          }
          helperText={t('m-invoice:original_copy.hint', {
            defaultValue:
              "Whether you'll return original PODs of these orders with this invoice. The status applies to every order on the invoice.",
          })}
        />

        <VatFileField
          id="eligible-vat-upload"
          files={vatFiles}
          onChange={setVatFiles}
          required={declaredInvoiceExists}
          helperText={
            declaredInvoiceExists
              ? t('m-invoice:create_invoice.vat_upload_required_helper', {
                  defaultValue:
                    "Required because you've supplied invoice details. Must be a PDF straight from your accounting system.",
                })
              : t('m-invoice:create_invoice.vat_upload_helper', {
                  defaultValue:
                    "Recommended for financing. Skip if you'll upload it later or your accounting system already issued it.",
                })
          }
        />
      </div>
    ),
  };

  return (
    <EditDrawer
      isOpen={open}
      onClose={() => onOpenChange(false)}
      title={t('m-invoice:create_invoice.drawer_title', {
        defaultValue: 'Create invoice from delivered orders',
      })}
      steps={[selectStep, detailsStep]}
      currentStepId={step}
      onStepChange={id => setStep(id as StepId)}
      isSubmitting={captureLegs.isPending}
    />
  );
};

/**
 * Convenience wrapper that wires `EligibleLegsDrawer` to the
 * shared {@link useEligibleLegsDrawerStore}. Mount one instance at
 * the page root and any surface can call
 * `useEligibleLegsDrawerStore.getState().open({ clientId,
 * viewerOrgId })`.
 */
export const EligibleLegsDrawerHost: React.FC = () => {
  const { isOpen, clientId, viewerOrgId, close } = useEligibleLegsDrawerStore(
    s => ({
      isOpen: s.isOpen,
      clientId: s.clientId,
      viewerOrgId: s.viewerOrgId,
      close: s.close,
    }),
  );
  return (
    <EligibleLegsDrawer
      clientId={clientId}
      viewerOrgId={viewerOrgId}
      open={isOpen}
      onOpenChange={next => {
        if (!next) close();
      }}
    />
  );
};
